import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Prop, State, Watch } from '@stencil/core';
import { addComponentTokens } from '@workbench/load-utils/dist/tokens';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedDirectionService, SharedScrollLockService } from '../../global/workbench-global';
import { DebounceDelays } from '../../shared/debounce-constants';
import { Keys } from '../../shared/keys';
import { querySelectorAllAsArray } from '../../utils/query-selector-all-as-array';
import { throttle } from '../../utils/throttle';
import { getRandomId } from '../../utils/get-random-id';
import { Ratio } from '../aspect-ratio/ratio';
import { MEDIA_QUERY_SIZES } from '../../utils/services/media-query.service';

const MAGIC_THRESHOLD_PX = 5;

/** @slot - wb-gallery-item elements  */
@Component({
  tag: 'wb-gallery',
  styleUrl: './gallery.scss',
  shadow: true,
})
export class GalleryComponent implements ComponentInterface {
  private uniqueId = getRandomId();

  @Element()
  public el: HTMLElement;

  /** set to true to hide the fullscreen button */
  @Prop({ reflect: true })
  public preventFullscreenMode: boolean = false;

  /** set to true to prevent exiting fullscreen mode on escape key */
  @Prop({ reflect: true })
  public disableEscapeKey: boolean = false;

  /** aspect ratio */
  @Prop({ reflect: true })
  public ratio: Ratio = '16x9';

  /** Controls if gallery is in fullscreen mode */
  @Prop({ reflect: true })
  public fullscreen: boolean = false;

  /** sets aria-label for button that enters fullscreen mode */
  @Prop()
  public enterFullscreenAriaLabel!: string;

  /** sets aria-label for button that exits fullscreen mode */
  @Prop()
  public exitFullscreenAriaLabel!: string;

  /** sets aria-label of next button */
  @Prop()
  public nextButtonAriaLabel!: string;

  /** sets aria-label of previous button */
  @Prop()
  public previousButtonAriaLabel!: string;

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  /** event emitted when current item changes (first item = 1) */
  @Event()
  public wbchange!: EventEmitter<number>;

  @State()
  public scrolledToStart: boolean = true;

  @State()
  public scrolledToEnd: boolean = false;

  @State()
  private items: Array<HTMLWbGalleryItemElement> = [];

  @State()
  private currentItem: number = 1;

  @State()
  private isSmall: boolean = false;

  /** Emitted when fullscreen toggles */
  @Event()
  public wbtogglefullscreen!: EventEmitter<boolean>;

  /** Emitted when previous button is clicked */
  @Event()
  public wbgallerypreviousclick!: EventEmitter<void>;

  /** Emitted when next button is clicked */
  @Event()
  public wbgallerynextclick!: EventEmitter<void>;

  /** Emitted when a thumbnail is clicked */
  @Event()
  public wbgallerythumbclick!: EventEmitter<number>;

  private ro: ResizeObserver;

  @Listen('keyup', { target: 'window' })
  public onKeyUp(e: KeyboardEvent): void {
    if (e.key === Keys.ESC && this.fullscreen && !this.disableEscapeKey) {
      this.toggleFullscreen();
    }
  }

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  private TagNames = transformTagNames(this, 'wb-gallery', ['wb-round-button', 'wb-icon', 'wb-aspect-ratio', 'wb-gallery-item', 'wb-video']);

  private mainSliderEl: HTMLElement;
  private thumbnailSliderEl: HTMLElement;
  private multiplier: number = this.isRtl ? -1 : 1;

  public async connectedCallback(): Promise<void> {
    await addComponentTokens(this.el.shadowRoot);
    this.updateDirection();
  }

  private scrollToNextPage = (): void => {
    const scrollDist = this.isRtl ? -this.mainSliderEl.clientWidth : this.mainSliderEl.clientWidth;
    this.mainSliderEl.scroll({ left: this.mainSliderEl.scrollLeft + scrollDist, behavior: 'smooth' });
    this.wbgallerynextclick.emit();
  };

  private scrollToPreviousPage = (): void => {
    const scrollDist = this.isRtl ? this.mainSliderEl.clientWidth : -this.mainSliderEl.clientWidth;
    this.mainSliderEl.scroll({ left: this.mainSliderEl.scrollLeft + scrollDist, behavior: 'smooth' });
    this.wbgallerypreviousclick.emit();
  };

  public componentDidLoad(): void {
    this.ro = new ResizeObserver((entries) => {
      this.isSmall = entries[0].contentRect.width < MEDIA_QUERY_SIZES.mq2;
    });
    this.ro.observe(this.el);
    this.hasVideo();
  }

  @Watch('fullscreen')
  public onFullscreenChange(newValue: boolean, oldValue: boolean): void {
    if (newValue !== oldValue) {
      this.updateScrollLock();
      this.wbtogglefullscreen.emit(newValue);
    }
  }

  private toggleFullscreen = (): void => {
    this.fullscreen = !this.fullscreen;
  };

  private updateScrollLock(): void {
    if (this.fullscreen && !SharedScrollLockService.isLocked) {
      SharedScrollLockService.lock(this.uniqueId);
    } else if (!this.fullscreen && SharedScrollLockService.isLocked) {
      SharedScrollLockService.unlock(this.uniqueId);
    }
  }

  private onMainSliderChange = (): void => {
    const itemToScrollTo = this.thumbnailSliderEl.querySelector(`[data-index='${this.currentItem - 1}']`) as HTMLElement;
    this.thumbnailSliderEl.scroll({ left: itemToScrollTo.offsetLeft, behavior: 'smooth' });
  };

  private onThumbnailClick = (event: MouseEvent | KeyboardEvent): void => {
    const index = Number((event.target as HTMLElement).dataset.index);

    this.mainSliderEl.scrollTo({ left: index * this.mainSliderEl.clientWidth * this.multiplier, behavior: 'smooth' });
    this.wbgallerythumbclick.emit(index);
  };

  private onThumbnailKeyDown = (event: KeyboardEvent): void => {
    if (event.code === 'Enter' || event.code === 'Space') {
      event.preventDefault();
      this.onThumbnailClick(event);
    }
  };

  private onSlotChange = (): void => {
    this.items = querySelectorAllAsArray<HTMLWbGalleryItemElement>(this.el, this.TagNames.WbGalleryItem);
    this.items.forEach((item) => (item.ratio = this.ratio));
  };

  private scrollUpdate = (): void => {
    this.updateArrows();
    this.updateCurrentItem();
  };

  private updateArrows = (): void => {
    const direction = this.isRtl ? -1 : 1;
    const scrollLeft = this.mainSliderEl.scrollLeft * direction;
    const scrollWidth = this.mainSliderEl.scrollWidth;

    this.scrolledToStart = scrollLeft < MAGIC_THRESHOLD_PX;
    this.scrolledToEnd = scrollLeft + this.mainSliderEl.clientWidth + MAGIC_THRESHOLD_PX > scrollWidth;
  };

  private getFirstVisibleItem(items: Array<HTMLElement>): HTMLElement {
    const edges = this.mainSliderEl.getBoundingClientRect();

    return items.find((item: HTMLElement) => {
      const coords = item.getBoundingClientRect();

      return this.isRtl //
        ? coords.x + coords.width / 2 <= edges.right
        : coords.x + coords.width / 2 >= edges.left;
    });
  }

  private updateCurrentItem = (): void => {
    const previousItem = this.currentItem;
    const firstVisibleItem = this.getFirstVisibleItem(this.items) as HTMLWbGalleryItemElement;
    this.currentItem = this.items.indexOf(firstVisibleItem) + 1;

    if (this.currentItem !== previousItem) {
      this.onMainSliderChange();
      this.wbchange.emit(this.currentItem);
    }
  };

  private throttledUpdate = throttle(() => this.scrollUpdate(), DebounceDelays.Scroll);

  private bindMailSliderEl = (el: HTMLDivElement): void => {
    this.mainSliderEl = el;
  };

  private bindThumbnailSliderEl = (el: HTMLDivElement): void => {
    this.thumbnailSliderEl = el;
  };

  /** Preventing fullscreen button if video is present */
  private hasVideo = (): void => {
    const { WbVideo } = this.TagNames;
    const hasVideo = this.items.find((element) => element.shadowRoot.querySelector(WbVideo));
    if (hasVideo) {
      this.preventFullscreenMode = true;
    }
  };

  public render(): JSX.Element {
    const { WbRoundButton, WbAspectRatio, WbIcon } = this.TagNames;

    const hasMultipleItems = this.items.length > 1;
    const hasLeftArrow = !this.scrolledToStart && hasMultipleItems;
    const hasRightArrow = !this.scrolledToEnd && hasMultipleItems;

    return (
      <Host class={`wb-gallery ${this.isSmall ? 'is-small' : ''}`}>
        <div class="inner">
          <div class="main" onScroll={this.throttledUpdate} ref={this.bindMailSliderEl} tabindex="0">
            <slot onSlotchange={this.onSlotChange} />
          </div>
          <div class="controls" style={{ '--gallery-ratio': this.ratio.replace('x', '/') }}>
            {hasLeftArrow && (
              <WbRoundButton size={this.isSmall ? 'new-m' : 'new-l'} elevation="l" class="arrow is-previous" onClick={this.scrollToPreviousPage} button-aria-label={this.previousButtonAriaLabel}>
                <WbIcon name="chevron-left/24" flip-rtl />
                {this.previousButtonAriaLabel}
              </WbRoundButton>
            )}
            {hasRightArrow && (
              <WbRoundButton size={this.isSmall ? 'new-m' : 'new-l'} elevation="l" class="arrow is-next" onClick={this.scrollToNextPage} button-aria-label={this.nextButtonAriaLabel}>
                <WbIcon name="chevron-right/24" flip-rtl />
                {this.nextButtonAriaLabel}
              </WbRoundButton>
            )}
            {!this.preventFullscreenMode && (
              <WbRoundButton size={this.isSmall ? 'new-m' : 'new-l'} elevation="xs" data-on="dark-image" class="toggle-fullscreen-button" onClick={this.toggleFullscreen} button-aria-label={this.fullscreen ? this.exitFullscreenAriaLabel : this.enterFullscreenAriaLabel}>
                <WbIcon name={this.fullscreen ? 'bds/fullscreen-exit/24' : 'bds/fullscreen/24'} />
                {this.fullscreen ? this.exitFullscreenAriaLabel : this.enterFullscreenAriaLabel}
              </WbRoundButton>
            )}
          </div>

          {hasMultipleItems && (
            <div class="thumbs" ref={this.bindThumbnailSliderEl}>
              {this.items.map((el, index) => (
                <WbAspectRatio
                  ratio={this.ratio}
                  data-index={index}
                  tabindex="0"
                  role="button"
                  onKeyDown={this.onThumbnailKeyDown}
                  onClick={this.onThumbnailClick}
                  class={{
                    thumb: true,
                    'is-active': index === this.currentItem - 1,
                  }}
                >
                  <img src={el.src || el.videoThumb} srcset={el.srcSet} alt={el.alt} />
                </WbAspectRatio>
              ))}
            </div>
          )}
        </div>
        {this.fullscreen && !this.preventFullscreenMode && (
          <WbRoundButton size="new-l" data-on="contrast" class="exit-fullscreen-button" onClick={this.toggleFullscreen} button-aria-label={this.exitFullscreenAriaLabel}>
            <WbIcon name="close/24" />
            {this.exitFullscreenAriaLabel}
          </WbRoundButton>
        )}
      </Host>
    );
  }
}
